import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, TicketMinus, Target } from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Story = {
  client: string;
  result: string;
  metric: string;
  metricLabel: string;
  trend: "up" | "down";
  bars: number[];
  icon: React.ReactNode;
};

const stories: Story[] = [
  {
    client: "Ecommerce Store",
    result: "Automated product recommendations and checkout assistance.",
    metric: "+35%",
    metricLabel: "Increase in Sales",
    trend: "up",
    bars: [30, 42, 48, 58, 72, 85, 98],
    icon: <TrendingUp size={20} aria-hidden="true" />,
  },
  {
    client: "Healthcare Provider",
    result: "AI triage bot handled appointment scheduling and FAQs.",
    metric: "-60%",
    metricLabel: "Support Tickets",
    trend: "down",
    bars: [95, 82, 70, 58, 45, 38, 32],
    icon: <TicketMinus size={20} aria-hidden="true" />,
  },
  {
    client: "Travel Agency",
    result: "Lead qualification bot captured and nurtured travel inquiries.",
    metric: "+40%",
    metricLabel: "Lead Conversion",
    trend: "up",
    bars: [28, 38, 45, 55, 68, 78, 92],
    icon: <Target size={20} aria-hidden="true" />,
  },
];

const ChatBotSuccessStories: React.FC = () => (
  <AnimatedSection as="section" className="cbot-section cbot-section--alt" aria-labelledby="cbot-stories-title">
    <div className="cbot-container">
      <SectionHeader
        eyebrow="Success Stories"
        title="Real Results from AI Chatbot Deployments"
        text="See how businesses achieved measurable growth with WebTech chatbot solutions."
      />

      <div className="cbot-stories-grid">
        {stories.map((story, index) => (
          <motion.article
            key={story.client}
            className="cbot-story-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ y: -8 }}
          >
            <div className="cbot-story-card__header">
              <span className="cbot-story-card__icon">{story.icon}</span>
              <h3>{story.client}</h3>
            </div>
            <p>{story.result}</p>
            <div className="cbot-story-card__metric">
              <strong className={story.trend === "up" ? "cbot-story-card__metric--up" : "cbot-story-card__metric--down"}>
                {story.metric}
              </strong>
              <span>{story.metricLabel}</span>
            </div>
            <div className="cbot-story-card__chart" aria-hidden="true">
              {story.bars.map((height, i) => (
                <span
                  key={i}
                  className={story.trend === "up" ? "cbot-story-card__bar--up" : "cbot-story-card__bar--down"}
                  style={{ height: `${height}%` }}
                />
              ))}
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </AnimatedSection>
);

export default ChatBotSuccessStories;
